import { useEffect, useRef } from 'react'
import { FormProvider } from 'react-hook-form'
import EditPaymentTable from './EditPaymentTable'
import type { EditPaymentsDialogState } from './EditPaymentsDialog'

interface EditPaymentsDialogBodyProps {
  state: EditPaymentsDialogState
  /** Closes the editor itself — what a tool's "return to calendar" ends up calling. */
  closeEditor: () => void
}

/**
 * The editor's body: the payment table, and the one tool an opener asked to run on top of it.
 * A component rather than an inline slot because that default tool has to open *once*, on
 * mount, with a close that reaches the editor — neither of which a render callback can do.
 */
const EditPaymentsDialogBody = ({ state, closeEditor }: EditPaymentsDialogBodyProps) => {
  const { defaultTool, defaultBatchUpdate, isLoadingDefaultValues, runTool } = state
  const startedRef = useRef(false)

  // Not before the table's rows have loaded: the tools that work off the scope — spreading an
  // overdue, catching one up — would otherwise open on an empty selection.
  useEffect(() => {
    if (startedRef.current || !defaultTool || isLoadingDefaultValues) return
    startedRef.current = true
    void runTool(defaultTool, closeEditor, defaultBatchUpdate)
  }, [closeEditor, defaultBatchUpdate, defaultTool, isLoadingDefaultValues, runTool])

  return (
    <FormProvider {...state.formMethods}>
      <EditPaymentTable
        payments={state.payments}
        onDelete={state.deletePayment}
        onDeleteAll={state.deleteAllPayments}
        onRestore={state.restorePayment}
        onCriticalChange={state.onCriticalChange}
        onUncriticalChange={state.onUncriticalChange}
        paymentMethodOptions={state.paymentMethodOptions}
        baseline={state.baseline}
        loading={state.isLoadingDefaultValues}
        placeholderRowCount={state.selectedPaymentCount}
        recomputing={state.isRecomputing}
        disabled={state.isLockedByOther}
        loanActivationDate={state.activationOn}
      />
    </FormProvider>
  )
}

export default EditPaymentsDialogBody
